import React from 'react';
import clsx from 'clsx';
import { CheckCircle2, Truck, Package, AlertTriangle } from 'lucide-react';
import {
  describeActivity,
  formatRelativeTime,
} from '../../../../../features/dashboard/dashboardUtils';

const TONE_ICON = {
  success: CheckCircle2,
  warning: Truck,
  danger: AlertTriangle,
  info: Package,
};

const TONE_CLASS = {
  success: 'bg-ninja-greenLight text-ninja-green',
  warning: 'bg-orange-100 text-warning dark:bg-orange-900/20',
  danger: 'bg-red-100 text-danger dark:bg-red-900/20',
  info: 'bg-blue-100 text-info dark:bg-blue-900/20',
};

/**
 * The last movements, one per shipment.
 *
 * Read from `DeliveryStatusHistory`, which records what changed but not who
 * changed it — there is no user FK on that table — so every line is about the
 * shipment. Clicking one opens its order.
 *
 * @param {object} props
 * @param {Array<object>} props.activity
 * @param {(orderId: number) => void} props.onEntryClick
 * @param {boolean} props.isLoading
 */
export const RecentActivityCard = ({ activity = [], onEntryClick, isLoading }) => (
  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border">
    <h2 className="text-base font-bold text-ninja-dark dark:text-white mb-5">
      Actividad Reciente
    </h2>

    {isLoading && activity.length === 0 && (
      <div className="flex flex-col gap-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-9 rounded-xl bg-ninja-gray dark:bg-[#2d2d2d] animate-pulse" />
        ))}
      </div>
    )}

    {!isLoading && activity.length === 0 && (
      <p className="text-sm text-text-muted">Sin movimientos en el periodo seleccionado</p>
    )}

    <div className="flex flex-col space-y-4">
      {activity.map((entry) => {
        const { tone, text } = describeActivity(entry);
        const Icon = TONE_ICON[tone];

        return (
          <button
            key={entry.delivery_id}
            onClick={() => onEntryClick(entry.order_id)}
            className="flex gap-3 items-start text-left group"
          >
            <div
              className={clsx(
                'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0',
                TONE_CLASS[tone]
              )}
            >
              <Icon size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-ninja-dark dark:text-white leading-tight mb-1 group-hover:text-ninja-green transition-colors">
                {text}
              </div>
              <div className="text-xs text-text-muted">
                {formatRelativeTime(entry.date)}
                {entry.reference_id ? ` · ${entry.reference_id}` : ''}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);
